<template>
  <div class="template-detail-page" v-loading="loading">
    <WelcomeBanner :title="template.template_name || '模板详情'" :subtitle="template.template_desc || ''">
      <div slot="right">
        <el-button type="text" icon="el-icon-arrow-left" size="small" class="back-button" @click="goBack">返回列表</el-button>
        <el-button type="primary" @click="editTemplate">编辑模板</el-button>
      </div>
    </WelcomeBanner>

    <BaseCard>
      <el-row :gutter="20" class="detail-content">
      <el-col :span="16">
        <!-- 模板描述 -->
        <el-card class="info-card">
          <div slot="header" class="card-header">
            <span class="header-title">模板信息</span>
          </div>
          <div class="template-info">
            <div class="desc-section">
              <h3>描述</h3>
              <p class="desc-text">{{ template.template_desc || '暂无描述' }}</p>
            </div>
            <el-divider></el-divider>
            <div class="meta-info">
              <div class="meta-item">
                <i class="el-icon-time"></i>
                <span>创建于 {{ formatTime(template.create_time) }}</span>
              </div>
              <div class="meta-item" v-if="template.update_time">
                <i class="el-icon-edit"></i>
                <span>更新于 {{ formatTime(template.update_time) }}</span>
              </div>
            </div>
          </div>
        </el-card>

        <!-- 步骤列表 -->
        <el-card class="steps-card" style="margin-top: 20px;">
          <div slot="header" class="card-header">
            <span class="header-title">步骤列表</span>
            <span class="step-count" v-if="steps.length">{{ steps.length }} 个步骤</span>
          </div>
          <div v-if="steps.length" class="steps-list">
            <div v-for="(step, index) in steps" :key="step.step_id" class="step-item">
              <!-- 步骤连线 -->
              <div v-if="index > 0" class="step-connector">
                <i class="el-icon-bottom"></i>
              </div>
              <!-- 步骤卡片 -->
              <el-card class="step-card" shadow="hover" @click.native="showStepDetail(step)">
                <div class="step-header">
                  <div class="step-badge">
                    <span class="step-index">{{ index + 1 }}</span>
                    <span v-if="step.step_code" class="step-code">{{ step.step_code }}</span>
                  </div>
                  <div class="step-actions">
                    <el-tag :type="step.status === 1 ? 'success' : 'danger'" size="mini" effect="plain">
                      {{ step.status === 1 ? '启用' : '禁用' }}
                    </el-tag>
                  </div>
                </div>
                <h4 class="step-name">{{ step.step_name }}</h4>
                <p class="step-desc" v-if="step.step_desc">{{ step.step_desc }}</p>
                <div class="step-footer">
                  <span class="step-time">
                    <i class="el-icon-time"></i> {{ formatTime(step.create_time) }}
                  </span>
                  <span v-if="step.retry_count > 0" class="step-retry">
                    <i class="el-icon-refresh"></i> 重试 {{ step.retry_count }} 次
                  </span>
                  <span v-if="step.next_steps_list && step.next_steps_list.length" class="step-next">
                    <i class="el-icon-right"></i> 后续 {{ step.next_steps_list.length }} 步
                  </span>
                </div>
              </el-card>
            </div>
          </div>
          <div v-else class="empty-state">
            <i class="el-icon-document" style="font-size: 48px; color: #dcdfe6;"></i>
            <p>暂无步骤定义</p>
          </div>
        </el-card>

        <!-- 步骤详情抽屉 -->
        <el-drawer
          title="步骤详情"
          :visible.sync="stepDrawerVisible"
          size="500px"
          direction="rtl"
        >
          <div v-if="currentStep" class="step-detail">
            <el-descriptions :column="1" border>
              <el-descriptions-item label="步骤ID">
                <code class="step-id">{{ currentStep.step_id }}</code>
              </el-descriptions-item>
              <el-descriptions-item label="所属模板">
                <code class="step-id">{{ currentStep.template_id }}</code>
              </el-descriptions-item>
              <el-descriptions-item label="步骤编号">
                <el-tag size="small">{{ currentStep.step_code }}</el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="步骤名称">{{ currentStep.step_name }}</el-descriptions-item>
              <el-descriptions-item label="状态">
                <el-tag :type="currentStep.status === 1 ? 'success' : 'danger'" size="small">
                  {{ currentStep.status === 1 ? '启用' : '禁用' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="允许重试次数">{{ currentStep.retry_count || 0 }} 次</el-descriptions-item>
              <el-descriptions-item label="创建时间">{{ formatTime(currentStep.create_time) }}</el-descriptions-item>
              <el-descriptions-item label="更新时间">{{ formatTime(currentStep.update_time) }}</el-descriptions-item>
            </el-descriptions>

            <div class="detail-section" v-if="currentStep.step_desc">
              <h4>步骤说明</h4>
              <div class="detail-content">{{ currentStep.step_desc }}</div>
            </div>

            <div class="detail-section" v-if="currentStep.next_steps_list && currentStep.next_steps_list.length">
              <h4>后续步骤</h4>
              <div class="next-steps">
                <el-tag v-for="ns in currentStep.next_steps_list" :key="ns" size="small" type="info">
                  {{ ns }}
                </el-tag>
              </div>
            </div>
          </div>
        </el-drawer>
      </el-col>

      <el-col :span="8">
        <!-- 模板信息侧边栏 -->
        <el-card class="info-sidebar">
          <div slot="header" class="card-header">
            <span class="header-title">基本信息</span>
          </div>
          <div class="info-list">
            <div class="info-item">
              <span class="label">模板ID</span>
              <span class="value code">{{ template.template_id }}</span>
            </div>
            <div class="info-item">
              <span class="label">模板名称</span>
              <span class="value">{{ template.template_name }}</span>
            </div>
            <div class="info-item">
              <span class="label">模板类型</span>
              <span class="value">
                <el-tag :type="template.project_id ? 'warning' : 'success'" size="mini">
                  {{ template.project_id ? '项目模板' : '全局模板' }}
                </el-tag>
              </span>
            </div>
            <div class="info-item">
              <span class="label">状态</span>
              <span class="value">
                <el-tag :type="template.status === 1 ? 'success' : 'danger'" size="mini">
                  {{ template.status === 1 ? '启用' : '禁用' }}
                </el-tag>
              </span>
            </div>
            <div class="info-item">
              <span class="label">关联项目</span>
              <span class="value">{{ template.project_id || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="label">创建时间</span>
              <span class="value">{{ formatTime(template.create_time) }}</span>
            </div>
            <div class="info-item">
              <span class="label">更新时间</span>
              <span class="value">{{ formatTime(template.update_time) || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="label">步骤数量</span>
              <span class="value">
                <el-badge :value="steps.length" type="primary" class="step-badge-count" />
              </span>
            </div>
          </div>
        </el-card>

        <!-- 步骤概览 -->
        <el-card class="step-overview" style="margin-top: 20px;">
          <div slot="header" class="card-header">
            <span class="header-title">步骤概览</span>
          </div>
          <div class="overview-content">
            <div class="overview-item">
              <div class="overview-icon enabled">
                <i class="el-icon-circle-check"></i>
              </div>
              <div class="overview-info">
                <span class="overview-value">{{ enabledStepsCount }}</span>
                <span class="overview-label">启用步骤</span>
              </div>
            </div>
            <div class="overview-item">
              <div class="overview-icon disabled">
                <i class="el-icon-circle-close"></i>
              </div>
              <div class="overview-info">
                <span class="overview-value">{{ disabledStepsCount }}</span>
                <span class="overview-label">禁用步骤</span>
              </div>
            </div>
          </div>
        </el-card>

        <!-- 操作按钮 -->
        <el-card class="action-card" style="margin-top: 20px;">
          <div class="action-buttons">
            <el-button type="primary" icon="el-icon-edit" style="width: 100%; margin-bottom: 12px;" @click="editTemplate">
              编辑模板
            </el-button>
            <el-button style="width: 100%; margin-bottom: 12px;" @click="toggleStatus">
              <i :class="template.status === 1 ? 'el-icon-close' : 'el-icon-check'"></i>
              {{ template.status === 1 ? '禁用模板' : '启用模板' }}
            </el-button>
            <el-button type="danger" icon="el-icon-delete" plain style="width: 100%;" @click="deleteTemplate">
              删除模板
            </el-button>
          </div>
        </el-card>
      </el-col>
    </el-row>
    </BaseCard>
  </div>
</template>

<script>
import { getTemplateDetail, changeTemplateStatus, deleteTemplate } from '../api/template'
import BaseCard from '@/components/BaseCard'
import WelcomeBanner from '@/components/WelcomeBanner'

export default {
  name: 'TemplateDetail',
  components: { BaseCard, WelcomeBanner },
  data() {
    return {
      templateId: null,
      template: {},
      steps: [],
      loading: false,
      stepDrawerVisible: false,
      currentStep: null
    }
  },
  computed: {
    enabledStepsCount() {
      return this.steps.filter(s => s.status === 1).length
    },
    disabledStepsCount() {
      return this.steps.filter(s => s.status === 0).length
    }
  },
  created() {
    this.templateId = this.$route.params.id
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const data = await getTemplateDetail(this.templateId)
        if (data) {
          this.template = data.template || data
          this.steps = data.steps || []
        }
      } catch (error) {
        console.error('加载模板详情失败:', error)
        this.$message.error('加载模板详情失败')
      } finally {
        this.loading = false
      }
    },
    formatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    goBack() {
      this.$router.push('/templates')
    },
    showStepDetail(step) {
      this.currentStep = step
      this.stepDrawerVisible = true
    },
    editTemplate() {
      this.$router.push({ name: 'StepTemplate', query: { edit: this.templateId } })
    },
    async toggleStatus() {
      try {
        const newStatus = this.template.status === 1 ? 0 : 1
        await this.$confirm(`确定要${newStatus === 1 ? '启用' : '禁用'}该模板吗？`, '提示', {
          type: 'warning'
        })
        await changeTemplateStatus(this.templateId, { status: newStatus })
        this.$message.success('状态更新成功')
        this.template.status = newStatus
      } catch (error) {
        if (error !== 'cancel') {
          console.error('更新状态失败:', error)
        }
      }
    },
    async deleteTemplate() {
      try {
        await this.$confirm('确定要删除该模板吗？此操作不可恢复。', '警告', {
          type: 'warning',
          confirmButtonText: '确定',
          cancelButtonText: '取消'
        })
        await deleteTemplate(this.templateId)
        this.$message.success('删除成功')
        this.$router.push('/templates')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除模板失败:', error)
          this.$message.error('删除失败')
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.template-detail-page {
  padding: 20px;
  min-height: calc(100vh - 84px);
  background-color: var(--bg-page);
}

.page-header {
  margin-bottom: 20px;

  .header-content {
    display: flex;
    align-items: center;
    gap: 12px;

    .template-name {
      font-size: 18px;
      font-weight: 600;
      color: var(--text-primary);
    }
  }
}

.detail-content {
  margin: 0 !important;
}

/* 卡片通用样式 */
.info-card,
.steps-card,
.info-sidebar,
.step-overview,
.action-card {
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.step-count {
  font-size: 13px;
  color: var(--text-muted);
}

/* 模板信息 */
.template-info {
  padding: 8px 0;

  .desc-section {
    h3 {
      margin: 0 0 12px 0;
      font-size: 14px;
      color: var(--text-secondary);
      font-weight: 500;
    }

    .desc-text {
      color: var(--text-primary);
      line-height: 1.6;
      margin: 0;
    }
  }

  .meta-info {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;

    .meta-item {
      display: flex;
      align-items: center;
      gap: 6px;
      color: var(--text-muted);
      font-size: 13px;

      i {
        font-size: 14px;
      }
    }
  }
}

/* 步骤列表 */
.steps-list {
  display: flex;
  flex-direction: column;
}

.step-item {
  position: relative;
}

.step-connector {
  display: flex;
  justify-content: center;
  padding: 8px 0;
  color: var(--text-muted);

  i {
    font-size: 20px;
  }
}

.step-card {
  margin-bottom: 0;
  transition: all 0.3s;

  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateX(4px);
  }

  .step-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    .step-badge {
      display: flex;
      align-items: center;
      gap: 8px;

      .step-index {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: linear-gradient(135deg, var(--primary-color), #66b1ff);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        font-weight: 600;
      }

      .step-code {
        font-family: 'Monaco', 'Consolas', monospace;
        font-size: 12px;
        color: var(--primary-color);
        background: rgba(64, 158, 255, 0.1);
        padding: 2px 8px;
        border-radius: 4px;
      }
    }
  }

  .step-name {
    margin: 0 0 8px 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.4;
  }

  .step-desc {
    margin: 0 0 12px 0;
    color: var(--text-secondary);
    font-size: 13px;
    line-height: 1.6;
    max-height: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
  }

  .step-footer {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;

    .step-time,
    .step-retry {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: var(--text-muted);

      i {
        font-size: 13px;
      }
    }

    .step-retry {
      color: var(--warning-color, #e6a23c);
    }

    .step-next {
      color: var(--primary-color);
    }
  }
}

/* 信息侧边栏 */
.info-sidebar {
  .info-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .step-badge-count {
    ::v-deep .el-badge__content {
      top: -2px;
      right: 4px;
    }
  }
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 6px;

  .label {
    font-size: 12px;
    color: var(--text-muted);
  }

  .value {
    font-size: 14px;
    color: var(--text-primary);
    word-break: break-all;

    &.code {
      font-family: 'Monaco', 'Consolas', monospace;
      font-size: 11px;
      color: var(--text-secondary);
      background-color: var(--bg-page);
      padding: 4px 8px;
      border-radius: var(--radius-sm);
    }
  }
}

/* 步骤概览 */
.step-overview {
  .overview-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .overview-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: var(--bg-page);
    border-radius: var(--radius-md);

    .overview-icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;

      &.enabled {
        background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
        color: #2e7d32;
      }

      &.disabled {
        background: linear-gradient(135deg, #fce4ec, #f8bbd9);
        color: #c2185b;
      }
    }

    .overview-info {
      display: flex;
      flex-direction: column;

      .overview-value {
        font-size: 20px;
        font-weight: 600;
        color: var(--text-primary);
        line-height: 1.2;
      }

      .overview-label {
        font-size: 12px;
        color: var(--text-muted);
      }
    }
  }
}

/* 操作按钮 */
.action-card {
  .action-buttons {
    display: flex;
    flex-direction: column;
  }
}

/* 空状态 */
.empty-state {
  text-align: center;
  color: var(--text-disabled);
  padding: 40px 20px;

  p {
    margin-top: 12px;
    font-size: 14px;
  }
}

/* 步骤详情抽屉 */
.step-detail {
  padding: 20px;

  .step-id {
    font-family: 'Monaco', 'Consolas', monospace;
    font-size: 11px;
    color: var(--text-secondary);
    background: var(--bg-page);
    padding: 2px 6px;
    border-radius: 4px;
  }

  .detail-section {
    margin-top: 20px;

    h4 {
      margin: 0 0 12px 0;
      font-size: 14px;
      color: var(--text-primary);
      font-weight: 600;
    }

    .detail-content {
      color: var(--text-secondary);
      line-height: 1.6;
      font-size: 13px;
      background: var(--bg-page);
      padding: 12px;
      border-radius: var(--radius-md);
    }

    .next-steps {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
  }
}
</style>
